import { OptionRelatedInstituteSettings, ProctoringOption, ProctoringPlatforms } from "@verity/proctoring-option/types";
import { forwardRef, ReactNode, useEffect, useId, useRef, useState } from "react";
import {
	getProctoringPlatformName,
	PROCTORING_OPTION_TEXT,
	ProctoringOptionCategory,
} from "../proctoring-options/option-text/ProctoringOptionText";
import {
	DEFAULT_OPTION_RELATED_INSTITUTE_SETTINGS,
	getSupportedOptionValue,
	isOptionSupportedForPlatform,
} from "@verity/proctoring-option";
import i18nPackage from "../../i18n/i18n";
import {
	CoreCheckbox,
	CoreChip,
	CoreInfoIndicator,
	CoreNoResults,
	CoreRadioCard,
	CoreRadioCardIcon,
	CoreRadioCardSelectIcon,
} from "../../core";
import { Divider, Link, Tooltip, Typography } from "@mui/material";
import styles from "./PlatformStep.module.css";
import { Trans } from "react-i18next";
import UnsupportedModal from "../edit-quiz-modals/unsupportedModal";
import StepDescription, { StepWrapper } from "./CommonStepComponents";
import ExtensionIcon from "../images/platform/extension.svg?react";
import LockdownIcon from "../images/platform/lockdown.svg?react";
import BrowserIcon from "../images/platform/browser.svg?react";
import { CoreLoadingPage } from "../../core/progress-indicators/Spinner";
import { VerityInstituteSettings } from "../../types/common";
import { EditQuizPageProps } from "../editQuiz";
import { SUPPORTED_BROWSER_CONFIG, SUPPORTED_OS_CONFIG } from "./StepIconConfig";
import type { UseEditQuizNavigationReturn } from "../hooks/useEditQuizNavigation";
import { QuickSetupHighlight } from "../../setup-guide";
import { isBrowserPlatformOffered } from "../editQuizHelper";
import type { EditQuizType } from "../../types/common";
import { isOptionInactiveForPlatformCheck } from "./proctoringOptionGuards";

export default function PlatformStep({ forceChromeOsManagedGuestSession = false, ...props }: ModeOptionsProps) {
	return (
		<StepWrapper>
			<div className={styles.platformSection}>
				<ModeOptions forceChromeOsManagedGuestSession={forceChromeOsManagedGuestSession} {...props} />
			</div>
		</StepWrapper>
	);
}

type ModeOptionsProps = {
	forceChromeOsManagedGuestSession?: boolean;
	loading?: boolean;
	isInstitutionProfile?: boolean;
	optionRelatedInstituteSettings?: OptionRelatedInstituteSettings;
	verityInstituteSettings: VerityInstituteSettings;
	setupGuideState?: UseEditQuizNavigationReturn["setupGuideState"];
	quizType?: EditQuizType;
} & Required<Pick<EditQuizPageProps, "enforcedProfilesOnly" | "setProctoringOption" | "proctoringOption">>;

export function ModeOptions({
	forceChromeOsManagedGuestSession = false,
	loading,
	isInstitutionProfile = false,
	enforcedProfilesOnly,
	setProctoringOption,
	verityInstituteSettings,
	optionRelatedInstituteSettings = DEFAULT_OPTION_RELATED_INSTITUTE_SETTINGS,
	proctoringOption: option,
	setupGuideState,
	quizType,
}: ModeOptionsProps) {
	const [isExtensionEnabled, setIsExtensionEnabled] = useState<boolean>(
		Boolean(option.isExtension && verityInstituteSettings.allowExtensionPlatform),
	);
	const [isMobileEnabled, setIsMobileEnabled] = useState<boolean>(
		Boolean(option.isMobile && verityInstituteSettings.allowMobilePlatform),
	);
	const [isLockdownEnabled, setIsLockdownEnabled] = useState<boolean>(
		Boolean(option.isLockdown && verityInstituteSettings.allowLockdownPlatform),
	);
	const isBrowserPlatformAvailable = isBrowserPlatformOffered({
		quizType,
		allowBrowserPlatform: verityInstituteSettings.allowBrowserPlatform,
	});
	const [isBrowserEnabled, setIsBrowserEnabled] = useState<boolean>(
		Boolean(option.isBrowser && isBrowserPlatformAvailable),
	);
	const [isChromeOsEnabled, setIsChromeOsEnabled] = useState<boolean>(Boolean(!isLockdownEnabled || option.isChromeOs));
	const [newOptions, setNewOptions] = useState<typeof option>();
	const [platformUnsupportedOptionNames, setPlatformUnsupportedOptionNames] = useState<{
		[P in keyof ProctoringPlatforms | "none"]?: {
			[P in ProctoringOptionCategory]?: Array<{
				name: string;
				option: keyof ProctoringOption;
			}>;
		};
	}>();
	const [unsupportedModalOpen, setUnsupportedModalOpen] = useState(false);
	const initialLockdownMode = useRef(Boolean(option.isLockdown && verityInstituteSettings.allowLockdownPlatform));
	const initialOneScreenEnforcement = useRef(option.oneScreenEnforcementMode);

	useEffect(() => {
		const extensionChange = Boolean(option.isExtension && verityInstituteSettings.allowExtensionPlatform);
		const mobileChange = Boolean(option.isMobile && verityInstituteSettings.allowMobilePlatform);
		const lockdownChange = Boolean(option.isLockdown && verityInstituteSettings.allowLockdownPlatform);
		const browserChange = Boolean(option.isBrowser && isBrowserPlatformAvailable);
		const chromeOsChange = Boolean(extensionChange || option.isChromeOs);
		if (isExtensionEnabled !== extensionChange) {
			setIsExtensionEnabled(extensionChange);
		}
		if (isMobileEnabled !== mobileChange) {
			setIsMobileEnabled(mobileChange);
		}
		if (isLockdownEnabled !== lockdownChange) {
			setIsLockdownEnabled(lockdownChange);
		}
		if (isBrowserEnabled !== browserChange) {
			setIsBrowserEnabled(browserChange);
		}
		if (isChromeOsEnabled !== chromeOsChange) {
			setIsChromeOsEnabled(chromeOsChange);
		}
	}, [option, verityInstituteSettings, isBrowserPlatformAvailable]);

	useEffect(() => {
		const unsupportedOptionNames: typeof platformUnsupportedOptionNames = {};
		const newOption = { ...option };

		newOption.isExtension = isExtensionEnabled;
		newOption.isLockdown = isLockdownEnabled;
		newOption.isMobile = isMobileEnabled;
		newOption.isChromeOs = isChromeOsEnabled;
		newOption.isBrowser = isBrowserEnabled;

		if (!initialLockdownMode.current) {
			if (isLockdownEnabled && !isInstitutionProfile) {
				newOption.oneScreenEnforcementMode = "normal";
			} else {
				newOption.oneScreenEnforcementMode = initialOneScreenEnforcement.current;
			}
		}

		// do not show warning modal if platform options did not change
		const showModal =
			newOption.isExtension !== Boolean(option.isExtension && verityInstituteSettings.allowExtensionPlatform) ||
			newOption.isMobile !== Boolean(option.isMobile && verityInstituteSettings.allowMobilePlatform) ||
			newOption.isLockdown !== Boolean(option.isLockdown && verityInstituteSettings.allowLockdownPlatform) ||
			newOption.isBrowser !== Boolean(option.isBrowser && isBrowserPlatformAvailable);

		const activePlatformKeys: (keyof ProctoringPlatforms)[] = [];
		if (isExtensionEnabled) activePlatformKeys.push("isExtension");
		if (isLockdownEnabled) activePlatformKeys.push("isLockdown");
		if (isMobileEnabled) activePlatformKeys.push("isMobile");
		if (isBrowserEnabled) activePlatformKeys.push("isBrowser");

		Object.keys(newOption).forEach((k) => {
			const key = k as keyof ProctoringOption;
			const value = newOption[key];
			if (key === "enableManualEndProctoring") {
				return;
			}
			if (isOptionInactiveForPlatformCheck(key, newOption)) {
				return;
			}
			if (value !== undefined) {
				const supportedValue = getSupportedOptionValue(newOption, key, value);

				// Check if the other selected platforms support this option
				const isSupportedByAlternative = activePlatformKeys.some((p) => isOptionSupportedForPlatform(p, key, value));

				if (value !== supportedValue.value && !isSupportedByAlternative) {
					// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
					(newOption[key] as typeof supportedValue.value) = supportedValue.value;

					if (showModal) {
						const optionHeader = PROCTORING_OPTION_TEXT[key]?.header;
						const optionCategory = PROCTORING_OPTION_TEXT[key]?.category;
						if (optionHeader && optionCategory) {
							const text = `${optionHeader} ${getOptionModifierText(supportedValue.value, option[key])}`;
							const unsupportedOptionNamesEntry = {
								name: text,
								option: key,
							};
							const platform = supportedValue.platform ?? "none";
							if (!unsupportedOptionNames[platform]) {
								unsupportedOptionNames[platform] = { [optionCategory]: [] };
							} else if (!unsupportedOptionNames[platform]?.[optionCategory]) {
								unsupportedOptionNames[platform] = {
									...unsupportedOptionNames[platform],
									[optionCategory]: [],
								};
							}
							unsupportedOptionNames[platform]?.[optionCategory]?.push(unsupportedOptionNamesEntry);
						}
					}
				}
			}
		});

		if (showModal && Object.keys(unsupportedOptionNames).length > 0) {
			setPlatformUnsupportedOptionNames(unsupportedOptionNames);
			setUnsupportedModalOpen(true);
			setNewOptions(newOption);
		} else {
			setProctoringOption(newOption);
		}
	}, [isExtensionEnabled, isMobileEnabled, isLockdownEnabled, isChromeOsEnabled, isBrowserEnabled]);

	const handleExtensionChange = () => {
		setIsBrowserEnabled(false);
		if (isExtensionEnabled && isLockdownEnabled) {
			setIsExtensionEnabled(false);
		} else {
			setIsExtensionEnabled((prev) => {
				if (!prev) {
					setIsLockdownEnabled(false);
					setIsChromeOsEnabled(true);
				}
				return true;
			});
		}
	};

	const handleLockdownChange = () => {
		setIsBrowserEnabled(false);
		if (isExtensionEnabled && isLockdownEnabled) {
			setIsLockdownEnabled(false);
		} else {
			setIsLockdownEnabled((prev) => {
				if (!prev) setIsExtensionEnabled(false);
				return true;
			});
		}
	};

	const handleBrowserChange = () => {
		setIsBrowserEnabled(true);
		setIsExtensionEnabled(false);
		setIsLockdownEnabled(false);
		setIsMobileEnabled(false);
	};

	const onCloseUnsupportedModal = () => {
		setPlatformUnsupportedOptionNames(undefined);
		setNewOptions(undefined);
		setUnsupportedModalOpen(false);

		const newIsExtensionEnabled = Boolean(option.isExtension && verityInstituteSettings.allowExtensionPlatform);
		setIsExtensionEnabled(newIsExtensionEnabled);
		setIsLockdownEnabled(Boolean(option.isLockdown && verityInstituteSettings.allowLockdownPlatform));
		setIsMobileEnabled(Boolean(option.isMobile && verityInstituteSettings.allowMobilePlatform));
		setIsBrowserEnabled(Boolean(option.isBrowser && isBrowserPlatformAvailable));
		setIsChromeOsEnabled(Boolean(newIsExtensionEnabled || option.isChromeOs));
	};
	// Declare separately to make linter happy
	const instructionLinkAriaLabel = i18nPackage.t("editQuizPage.platformStep.ariaLabel");

	const fallbackRef = useRef<HTMLDivElement>(null);
	const extensionRef = useRef<HTMLDivElement>(null);

	const showHighlight = setupGuideState && setupGuideState.highlightTarget !== "next";
	const highlightRef = setupGuideState?.highlightTarget === "default" ? extensionRef : fallbackRef;

	// This step will not be shown if enforced profile
	if (enforcedProfilesOnly) return null;

	const noPlatformsEnabled =
		!verityInstituteSettings.allowExtensionPlatform &&
		!verityInstituteSettings.allowLockdownPlatform &&
		!isBrowserPlatformAvailable;

	if (loading) return <CoreLoadingPage size={100} />;

	if (noPlatformsEnabled) {
		return (
			<CoreNoResults
				variant="no-results-variant"
				headerVariant="h2"
				header={i18nPackage.t("editQuizPage.platformStep.allDisabled")}
			/>
		);
	}

	return (
		<>
			<StepDescription>
				<Trans
					i18n={i18nPackage}
					i18nKey={"editQuizPage.platformStep.description"}
					components={{
						a: (
							<Link
								href="https://support.yuja.com/hc/en-us/articles/24085929932567-YuJa-Verity-Test-Proctoring-Overview#01JFTX1PEYZH76PBSADCB21J6C"
								target="_blank"
								rel="noreferrer"
								aria-label={instructionLinkAriaLabel}
							/>
						),
					}}
				/>
			</StepDescription>
			<div className={styles.proctorSettingsGridPlatforms} ref={fallbackRef}>
				{verityInstituteSettings.allowExtensionPlatform && (
					<ExtensionButton onClick={handleExtensionChange} checked={isExtensionEnabled} ref={extensionRef}>
						{verityInstituteSettings.allowMobilePlatform && isExtensionEnabled && (
							<MobileCheckBox isMobileEnabled={isMobileEnabled} setIsMobileEnabled={setIsMobileEnabled} />
						)}
					</ExtensionButton>
				)}
				{isBrowserPlatformAvailable && <BrowserButton onClick={handleBrowserChange} checked={isBrowserEnabled} />}
				{verityInstituteSettings.allowLockdownPlatform && (
					<LockdownButton onClick={handleLockdownChange} checked={isLockdownEnabled}>
						<div className={styles.checkboxes}>
							{verityInstituteSettings.allowMobilePlatform && isLockdownEnabled && (
								<MobileCheckBox isMobileEnabled={isMobileEnabled} setIsMobileEnabled={setIsMobileEnabled} />
							)}
							{isLockdownEnabled && !forceChromeOsManagedGuestSession && (
								<ChromeOsCheckbox
									checked={isChromeOsEnabled}
									onChange={setIsChromeOsEnabled}
									lockdownRequireManagedGuestSession={optionRelatedInstituteSettings.lockdownRequireManagedGuestSession}
								/>
							)}
						</div>
					</LockdownButton>
				)}
			</div>
			{unsupportedModalOpen && platformUnsupportedOptionNames && newOptions && (
				<UnsupportedModal
					open={unsupportedModalOpen}
					onClose={onCloseUnsupportedModal}
					onConfirm={() => {
						setProctoringOption(newOptions);
						setPlatformUnsupportedOptionNames(undefined);
						setNewOptions(undefined);
						setUnsupportedModalOpen(false);
					}}
					platformUnsupportedOptionNames={platformUnsupportedOptionNames}
				/>
			)}
			{setupGuideState && showHighlight && (
				<QuickSetupHighlight targetRef={highlightRef} position={"right"} description={setupGuideState.description} />
			)}
		</>
	);
}

function getOptionModifierText<T extends Partial<ProctoringOption>>(
	newValue: T[keyof T],
	oldValue: T[keyof T],
): string {
	if (newValue === "tab" && oldValue === "system") {
		return i18nPackage.t("editQuizPage.platformStep.system");
	}
	if ((typeof newValue === "string" && newValue !== "off" && oldValue === "off") || newValue === true) {
		return i18nPackage.t("editQuizPage.platformStep.disabled");
	}
	return "";
}

type PlatformBadgeRowProps = {
	compact?: boolean;
};

type PlatformBadgeProps = {
	compact: boolean;
	icon: React.ReactElement;
	bg: string;
	color?: string;
	text: string;
};

function PlatformBadge({ compact, icon, bg, color, text }: PlatformBadgeProps) {
	if (compact) {
		return (
			<Tooltip title={text}>
				<div className={styles.platformBadgeCompact} style={{ backgroundColor: bg }}>
					{icon}
				</div>
			</Tooltip>
		);
	}

	return (
		<CoreChip className={styles.platformBadge} chipColor={bg} sx={{ color }} icon={icon}>
			{text}
		</CoreChip>
	);
}

function SupportedBrowsers({ compact = false }: PlatformBadgeRowProps) {
	const configs = Object.values(SUPPORTED_BROWSER_CONFIG);
	return (
		<div className={styles.badgeRow}>
			{configs.map(({ Icon, bgColor, color, name }) => (
				<PlatformBadge
					key={name}
					compact={compact}
					icon={<Icon aria-hidden="true" />}
					bg={bgColor}
					color={color}
					text={name}
				/>
			))}
		</div>
	);
}

function SupportedOS({ compact = false }: PlatformBadgeRowProps) {
	const configs = Object.values(SUPPORTED_OS_CONFIG);
	return (
		<div className={styles.badgeRow}>
			{configs.map(({ Icon, bgColor, color, name }) => (
				<PlatformBadge
					key={name}
					compact={compact}
					icon={<Icon aria-hidden="true" />}
					bg={bgColor}
					color={color}
					text={name}
				/>
			))}
		</div>
	);
}

type EditQuizButtonProps = {
	onClick: () => void;
	checked: boolean;
	title: string;
	description: React.ReactNode;
	subtitle: string;
	icon: React.ReactNode;
	supportedComponent: React.ReactNode;
	children?: React.ReactNode;
	className?: string;
	iconSize?: number;
};

const PlatformButton = forwardRef<HTMLDivElement, EditQuizButtonProps>(function PlatformButton(
	{ checked, onClick, description, icon, subtitle, supportedComponent, title, children, className, iconSize },
	ref,
) {
	return (
		<CoreRadioCard
			ref={ref}
			selected={checked}
			onClick={onClick}
			slotProps={{
				container: { className: `${styles.platformButtonWrapper} ${className ?? ""}` },
				button: { className: styles.platformButton },
				actionWrapper: { className: styles.contentWrapper },
			}}
			secondaryAction={
				children && checked ? (
					<>
						<Divider className={styles.platformDivider} />
						{children}
					</>
				) : null
			}
		>
			<CoreRadioCardIcon
				enabled={checked}
				icon={icon}
				className={styles.platformIcon}
				width={iconSize}
				height={iconSize}
			/>
			<div className={styles.platformInfoWrapper}>
				<div className={styles.platformColWrapper}>
					<div className={styles.platformTitleCheckmarkWrapper}>
						<Typography variant="h3" className={styles.title}>
							{title}
						</Typography>
						<CoreRadioCardSelectIcon selected={checked} />
					</div>
					<Typography className={styles.description}>{description}</Typography>
				</div>
				<div className={styles.platformColWrapper}>
					<Typography className={styles.subtitle}>{subtitle}</Typography>
					{supportedComponent}
				</div>
			</div>
		</CoreRadioCard>
	);
});

const LockdownButton = forwardRef<HTMLDivElement, Pick<EditQuizButtonProps, "children" | "checked" | "onClick">>(
	function LockdownButton({ checked, onClick, children }, ref) {
		return (
			<PlatformButton
				ref={ref}
				title={getProctoringPlatformName("lockdown")}
				icon={<LockdownIcon aria-hidden="true" />}
				description={
					<Trans
						i18n={i18nPackage}
						i18nKey={"editQuizPage.platformStep.lockdown.description"}
						components={{ b: <strong /> }}
					/>
				}
				subtitle={i18nPackage.t("editQuizPage.platformStep.lockdown.subtitle")}
				supportedComponent={<SupportedOS />}
				checked={checked}
				onClick={onClick}
			>
				{children}
			</PlatformButton>
		);
	},
);

const ExtensionButton = forwardRef<HTMLDivElement, Pick<EditQuizButtonProps, "children" | "checked" | "onClick">>(
	function ExtensionButton({ checked, onClick, children }, ref) {
		return (
			<PlatformButton
				ref={ref}
				title={getProctoringPlatformName("extension")}
				icon={<ExtensionIcon aria-hidden="true" />}
				description={
					<Trans
						i18n={i18nPackage}
						i18nKey={"editQuizPage.platformStep.extension.description"}
						components={{ b: <strong /> }}
					/>
				}
				subtitle={i18nPackage.t("editQuizPage.platformStep.extension.subtitle")}
				supportedComponent={<SupportedBrowsers />}
				checked={checked}
				onClick={onClick}
			>
				{children}
			</PlatformButton>
		);
	},
);

const BrowserButton = forwardRef<HTMLDivElement, Pick<EditQuizButtonProps, "children" | "checked" | "onClick">>(
	function BrowserButton({ checked, onClick, children }, ref) {
		return (
			<PlatformButton
				ref={ref}
				className={styles.browserPlatform}
				iconSize={36}
				title={getProctoringPlatformName("browser")}
				icon={<BrowserIcon aria-hidden="true" />}
				description={
					<Trans
						i18n={i18nPackage}
						i18nKey={"editQuizPage.platformStep.browser.description"}
						components={{ b: <strong /> }}
					/>
				}
				subtitle={i18nPackage.t("editQuizPage.platformStep.browser.subtitle")}
				supportedComponent={<SupportedBrowsers />}
				checked={checked}
				onClick={onClick}
			>
				{children}
			</PlatformButton>
		);
	},
);

function LabeledCheckbox({
	checked,
	onChange,
	header,
	hint,
	children,
}: {
	checked: boolean;
	onChange: (value: boolean) => void;
	header: ReactNode;
	hint?: ReactNode;
	children: ReactNode;
}) {
	const id = useId();
	return (
		<div className={styles.labeledCheckboxContainer}>
			<CoreCheckbox size="17px" onClick={() => onChange(!checked)} checked={checked} id={id} />

			<label className={styles.checkboxInfoWrapper} htmlFor={id}>
				<div className={styles.checkboxHeaderWrapper}>
					<Typography component={"span"} className={styles.protoredMethodTitleSub}>
						{header}
					</Typography>
					{hint && <CoreInfoIndicator hint={hint} size={20} />}
				</div>
				<Typography component="span" className={styles.protoredMethodDescriptionSub}>
					{children}
				</Typography>
			</label>
		</div>
	);
}

function MobileCheckBox({
	setIsMobileEnabled,
	isMobileEnabled,
}: {
	isMobileEnabled: boolean;
	setIsMobileEnabled: (value: boolean) => void;
}) {
	return (
		<LabeledCheckbox
			checked={isMobileEnabled}
			onChange={setIsMobileEnabled}
			header={i18nPackage.t("editQuizPage.platformStep.mobile.title")}
			hint={i18nPackage.t("editQuizPage.platformStep.mobile.hint")}
		>
			{i18nPackage.t("editQuizPage.platformStep.mobile.description")}
		</LabeledCheckbox>
	);
}

function ChromeOsCheckbox({
	lockdownRequireManagedGuestSession,
	checked,
	onChange,
}: {
	lockdownRequireManagedGuestSession: boolean;
	checked: boolean;
	onChange: (value: boolean) => void;
}) {
	return (
		<LabeledCheckbox
			checked={checked}
			onChange={onChange}
			header={i18nPackage.t("editQuizPage.platformStep.chromeOs.title")}
			hint={
				lockdownRequireManagedGuestSession
					? i18nPackage.t("editQuizPage.platformStep.chromeOs.hintManagedGuestSession")
					: i18nPackage.t("editQuizPage.platformStep.chromeOs.hintUnrestricted")
			}
		>
			{i18nPackage.t("editQuizPage.platformStep.chromeOs.description")}
		</LabeledCheckbox>
	);
}
